/**
 * The match as the screen holds it: stored ledger records, and the state you get by
 * folding them.
 *
 * There is no reducer over `MatchState` here and there must not be one. The ledger is the
 * only truth, so *every* number on screen — the header, the over strip, the building
 * scorecard — comes from one `replay()` (ADR-0002, ADR-0006). The screen's own state is
 * the record list and nothing else.
 *
 * **Every function here is pure and builds an event; none of them writes one.** That
 * separation is what makes "durably stored before the UI confirms" enforceable rather than
 * hoped for: the caller has to take the event this module returns, put it in the store, and
 * only then fold it into the draft. There is no path that reaches the screen without
 * passing through disk.
 *
 * `records` carry `syncedAt` rather than a bare event list, because two decisions need it —
 * the sync pill, and undo. See `undoLastBall`.
 */
import { asId, isDelivery, replay } from "@sportsos/engine";
import type {
  BatterHand,
  BatterRetired,
  BowlerChange,
  BowlingArm,
  BowlingType,
  Delivery,
  Event,
  EventId,
  InningsNo,
  InningsStart,
  MatchState,
  NewOver,
  PlayerId,
  Provenance,
  RuleWarning,
  Shot,
  UserId,
  Wicket,
} from "@sportsos/engine";
import { nextSeq } from "@sportsos/sync";
import type { StoredEvent } from "@sportsos/sync";
import type { Latch } from "./entry.ts";
import { buildRuns } from "./entry.ts";
import { newEventId } from "./ids.ts";
import type { MatchSetup } from "./setup.ts";
import { battingFirstTeamId, otherTeamId, teamById } from "./setup.ts";

/**
 * P0 writes exactly this: a human, fully confident, nothing else populated.
 *
 * `scorerId` comes from the signed-in user via `MatchSetup`, now that #14 puts the screen
 * behind the login gate — it is the field that makes a `ScorerHandoff` legible later, and
 * every ball says who tapped it.
 *
 * Still nullable, and that is deliberate rather than defensive: authorisation is against a
 * user id and credentials are optional by design (ADR-0008), so guest scoring in P1 (ID-6)
 * is a policy change at the gate and an unattributed ledger stays a valid state.
 */
const human = (scorerId: UserId | null): Provenance => ({
  source: "human",
  confidence: 1,
  deviceId: null,
  scorerId,
  confirmedBy: null,
  videoRef: null,
});

/**
 * The fields the ledger stamps on every event, which a caller never supplies.
 *
 * Spelled out rather than derived from `EventBase`, which `events.ts` deliberately does
 * not export — the base is an implementation detail of the union, not a public type.
 */
type LedgerFields =
  | "eventId"
  | "matchId"
  | "inningsNo"
  | "seq"
  | "supersedes"
  | "voided"
  | "provenance"
  | "recordedAt";

/** Distributive, so `Drafted<Event>` is a union of stripped members and not their
 *  intersection — a plain `Omit` over a union collapses to the shared keys. */
type Drafted<E> = E extends unknown ? Omit<E, LedgerFields> : never;

export interface MatchDraft {
  readonly setup: MatchSetup;
  /** Ledger records in append order, each carrying whether the server has acked it. */
  readonly records: readonly StoredEvent[];
}

/** The raw ledger, for the fold. */
export const ledgerEvents = (draft: MatchDraft): readonly Event[] =>
  draft.records.map((record) => record.event);

/** Fold a freshly stored event into the draft. Always un-acked: the outbox marks it later. */
export const commit = (draft: MatchDraft, event: Event): MatchDraft => ({
  ...draft,
  records: [...draft.records, { event, syncedAt: null, writtenBy: null }],
});

/** Adopt what the store says, on hydration and after every drain. */
export const withRecords = (
  draft: MatchDraft,
  records: readonly StoredEvent[],
): MatchDraft => ({ ...draft, records });

/** Everything needed to describe the next ball, once the crease is fully known. */
export interface Crease {
  readonly strikerId: PlayerId;
  readonly nonStrikerId: PlayerId;
  readonly bowlerId: PlayerId;
  readonly keeperId: PlayerId | null;
  readonly freeHit: boolean;
}

export const derive = (draft: MatchDraft): MatchState =>
  replay(ledgerEvents(draft), draft.setup.ruleSet, draft.setup.matchId);

/**
 * Open the match with the first innings.
 *
 * The openers and the opening bowler are not part of this: they arrive through the
 * same prompt gate that asks for a new batter after a wicket and a new bowler after an
 * over. One mechanism for "the ledger needs a fact before the next ball" means the
 * start of an innings, a fall of wicket and an innings break are the same code path.
 */
export const emptyDraft = (setup: MatchSetup): MatchDraft => ({ setup, records: [] });

export function buildFirstInnings(setup: MatchSetup): InningsStart {
  const battingTeamId = battingFirstTeamId(setup);
  return build<InningsStart>(emptyDraft(setup), 1, {
    type: "InningsStart",
    battingTeamId,
    bowlingTeamId: otherTeamId(setup, battingTeamId),
    oversAvailable: setup.ruleSet.overs,
    target: null,
    isSuperOver: false,
    followOn: false,
  });
}

/**
 * Build one event, stamped with its ledger fields. Pure: it writes nothing.
 *
 * **`seq` is the highest in the innings plus one, never the count.** It used to be the
 * count, which was correct while the draft was purely local and undo *popped* — but it
 * stops being correct the moment the outbox exists, because the server has already stored
 * what a pop removed. Reusing that address gets the next ball rejected with
 * `seq_conflict`, and the ledger's uniqueness constraint is what makes gap detection work
 * at all, so it cannot be relaxed to accommodate us. `nextSeq` is shared with the outbox
 * for exactly this reason: two answers to "which address is free" is a lost ball.
 */
export function build<E extends Event>(
  draft: MatchDraft,
  inningsNo: InningsNo,
  drafted: Drafted<E>,
  overrides: { readonly supersedes?: EventId; readonly voided?: boolean } = {},
): E {
  return {
    ...drafted,
    eventId: newEventId(),
    matchId: draft.setup.matchId,
    inningsNo,
    seq: nextSeq(draft.records, inningsNo),
    supersedes: overrides.supersedes ?? null,
    voided: overrides.voided ?? false,
    provenance: human(draft.setup.scorerId),
    recordedAt: new Date().toISOString(),
    // The one cast in the module. `Drafted<E>` plus the stamped fields *is* an `E` — the
    // ledger fields are exactly what was stripped — but the checker cannot conclude that
    // about a generic whose `Omit` was distributed over a union.
  } as unknown as E;
}

/**
 * Events that still apply: not voided, and not replaced by a later version.
 *
 * A one-hop check, deliberately simpler than the engine's `effectiveLedger`, which walks
 * chains of any depth. That is enough here because this is only ever asked about the *tail* —
 * what to undo, what a correction should supersede — and the tail of a chain is what a one-hop
 * filter returns. Anything needing the resolved history reads `derive(draft)` instead.
 */
export function liveEvents(draft: MatchDraft): readonly Event[] {
  const events = ledgerEvents(draft);
  const superseded = new Set(events.map((event) => event.supersedes).filter((id) => id !== null));
  return events.filter((event) => !event.voided && !superseded.has(event.eventId));
}

/** A new version of an event, marked void. The engine drops it during replay (ADR-0004). */
export function voidingCopy(draft: MatchDraft, original: Event): Event {
  return {
    ...original,
    eventId: newEventId(),
    seq: nextSeq(draft.records, original.inningsNo),
    supersedes: original.eventId,
    voided: true,
    recordedAt: new Date().toISOString(),
  } as Event;
}

/**
 * Undo the last ball — by appending superseding, voided versions of it *and* of the
 * bookkeeping that followed it.
 *
 * One tap, no confirmation (PRD §6.8). Returns the events to store, in order, or an empty
 * list when there is nothing to undo.
 *
 * **It appends rather than removes, and that is the fix for a ball-losing bug.** Popping the
 * event was right while the draft was purely local. Once the outbox exists the server may
 * already hold the ball, so a pop leaves the client and the server disagreeing about which
 * addresses are taken: the next ball reuses the popped `seq`, the server refuses it as
 * `seq_conflict`, and it is either dropped or rejected forever. Either way the product's
 * central promise breaks, and it takes no bad network to trigger — only an undo of a ball
 * that had already synced.
 *
 * Voiding is right for a ball that has *not* synced yet too, and uniformly so, which is why
 * there is one path and not two. Rewriting an un-synced event in place looks cheaper, but if
 * the original is in flight at that moment the server stores the un-voided version, dedupes
 * the rewrite on `eventId`, and the undo is silently lost. A new event with a new id cannot
 * be deduped away.
 *
 * **The trailing bookkeeping goes with it.** The batter who walked in after the wicket and
 * the bowler who took the next over came *after* the ball, so leaving them behind would undo
 * a delivery into a state that never existed — a new batter at the crease with the wicket
 * that brought him in gone.
 *
 * Nothing is deleted, which is what CONTEXT.md says a ledger is.
 */
export function undoLastBall(draft: MatchDraft): readonly Event[] {
  const live = liveEvents(draft);
  const lastBall = [...live].reverse().find(isDelivery);
  if (lastBall === undefined) return [];

  const from = live.indexOf(lastBall);
  const trailing = live.slice(from + 1);

  // If bookkeeping (a NewOver, BatterIn, etc.) sits after the last delivery with no
  // delivery between, the scorer's last *action* was that bookkeeping — not the ball.
  // Voiding only the trailing events undoes the bowler pick (or batter selection) and
  // lets the prompt gate re-ask, instead of reaching back and voiding a ball the scorer
  // did not intend to undo.
  const toVoid = trailing.length > 0 ? trailing : [live[from]!];

  const voids: Event[] = [];
  let working = draft;
  for (const event of toVoid) {
    const voiding = voidingCopy(working, event);
    voids.push(voiding);
    working = commit(working, voiding);
  }
  return voids;
}

export const canUndo = (draft: MatchDraft): boolean => liveEvents(draft).some(isDelivery);

// ── Before the first ball: the picks, and changing them one at a time ─────────

/**
 * What the scorer picked in this innings before its first ball, oldest first: the openers and
 * the first bowler. Empty once a ball is bowled, because from then on who batted and bowled is
 * history and changing it is a correction (ADR-0004), not a re-pick.
 */
export function picksBeforeFirstBall(draft: MatchDraft, state: MatchState): readonly Event[] {
  const innings = state.innings[state.innings.length - 1];
  if (innings === undefined || innings.ended !== null) return [];
  const live = liveEvents(draft).filter((event) => event.inningsNo === innings.inningsNo);
  if (live.some(isDelivery)) return [];
  return live.filter(
    (event) => event.type === "BatterIn" || event.type === "NewOver" || event.type === "BowlerChange",
  );
}

/** Whether ← Back has a pick to take back. */
export const canStepBack = (draft: MatchDraft, state: MatchState): boolean =>
  picksBeforeFirstBall(draft, state).length > 0;

/**
 * Take back the last pick only: the bowler, then the non-striker, then the striker, one tap
 * each. The question it answered is then asked again. Undoing all three to fix one was the
 * clumsy version this replaces; with ← Back on the questions themselves, nobody is stranded.
 */
export function stepBack(draft: MatchDraft, state: MatchState): readonly Event[] {
  const picks = picksBeforeFirstBall(draft, state);
  const last = picks[picks.length - 1];
  return last === undefined ? [] : [voidingCopy(draft, last)];
}

/** Undo on the pad: a pick before the first ball, otherwise the last ball (`undoLastBall`). */
export function undoLast(draft: MatchDraft, state: MatchState): readonly Event[] {
  return canStepBack(draft, state) ? stepBack(draft, state) : undoLastBall(draft);
}

export const canUndoLast = (draft: MatchDraft, state: MatchState): boolean =>
  canStepBack(draft, state) || canUndo(draft);

/** One of the three picks the Openers strip shows. */
export type OpenerSlot = "striker" | "nonStriker" | "bowler";

/**
 * Put someone else in one opening slot, leaving the other two as they are.
 *
 * The pick being replaced is voided and a new one recorded, the way undo works, so the ledger
 * reads "picked Arjun, then changed to Pant". A batter is recorded for the slot he is taking
 * rather than copying the replaced event's flag: the two agree before the first ball, but the
 * slot is what the scorer asked for, and the fold puts him at the end left empty. A new bowler
 * replaces every bowler pick before the first ball.
 *
 * Returns nothing to store when there is nothing to change: after the first ball, an empty
 * slot, the same player again, or the batter already at the other end.
 */
export function changeOpener(
  draft: MatchDraft,
  state: MatchState,
  slot: OpenerSlot,
  playerId: PlayerId,
  choice: { readonly hand?: BatterHand; readonly style?: BowlingStyleChoice; readonly overrideReason?: string | null } = {},
): readonly Event[] {
  const picks = picksBeforeFirstBall(draft, state);
  const header = state.header;
  if (picks.length === 0 || header === null) return [];
  const inningsNo = picks[0]!.inningsNo;

  if (slot === "bowler") {
    const bowlerPicks = picks.filter((event) => event.type === "NewOver" || event.type === "BowlerChange");
    const firstOver = bowlerPicks.find((event): event is NewOver => event.type === "NewOver");
    if (firstOver === undefined || header.bowler?.playerId === playerId) return [];
    const events: Event[] = [];
    let working = draft;
    for (const pick of bowlerPicks) {
      const voiding = voidingCopy(working, pick);
      events.push(voiding);
      working = commit(working, voiding);
    }
    events.push(buildNewOver(working, inningsNo, firstOver.overNo, playerId, choice.overrideReason ?? null, choice.style));
    return events;
  }

  const current = slot === "striker" ? header.striker?.playerId : header.nonStriker?.playerId;
  const other = slot === "striker" ? header.nonStriker?.playerId : header.striker?.playerId;
  if (current == null || playerId === current || playerId === other) return [];
  const replaced = [...picks].reverse().find((event) => event.type === "BatterIn" && event.playerId === current);
  if (replaced === undefined) return [];
  const voiding = voidingCopy(draft, replaced);
  return [voiding, buildBatterIn(commit(draft, voiding), inningsNo, playerId, slot === "striker", choice.hand)];
}

/**
 * The batters who have walked in since the last ball of this innings: the openers before the
 * first ball, or the new batter after a wicket. These are the only ends a scorer can still
 * change by simply saying so. Once a ball is bowled, who faced it is history, and changing it
 * is the "batters were the other way round" correction (ADR-0004).
 */
function incomingBatters(draft: MatchDraft, state: MatchState): readonly Event[] {
  const innings = state.innings[state.innings.length - 1];
  if (innings === undefined || innings.ended !== null) return [];
  const live = liveEvents(draft).filter((event) => event.inningsNo === innings.inningsNo);
  const lastBall = live.map((event) => isDelivery(event)).lastIndexOf(true);
  return live.slice(lastBall + 1).filter((event) => event.type === "BatterIn");
}

/** Whether the ⇄ Swap key does anything: both ends are filled and one was filled since the last ball. */
export const canSwapIncomingBatters = (draft: MatchDraft, state: MatchState): boolean =>
  state.header?.striker != null &&
  state.header.nonStriker != null &&
  incomingBatters(draft, state).length > 0;

/**
 * Put the batters who just came in at the other ends.
 *
 * Each incoming `BatterIn` is voided and recorded again with `takesStrike` flipped, in the
 * same order. The fold does the rest: a batter told to take the end that is occupied moves
 * the batter already there across (`replay.ts`, BatterIn). So the openers trade places, and
 * after a wicket the new batter and the survivor do. No new event type is needed, and the
 * ledger shows what happened: a pick, then a change of mind.
 */
export function swapIncomingBatters(draft: MatchDraft, state: MatchState): readonly Event[] {
  if (!canSwapIncomingBatters(draft, state)) return [];
  const incoming = incomingBatters(draft, state);
  const events: Event[] = [];
  let working = draft;
  for (const event of incoming) {
    const voiding = voidingCopy(working, event);
    events.push(voiding);
    working = commit(working, voiding);
  }
  for (const event of incoming) {
    if (event.type !== "BatterIn") continue;
    const again = buildBatterIn(working, event.inningsNo, event.playerId, !event.takesStrike, event.battingHand);
    events.push(again);
    working = commit(working, again);
  }
  return events;
}

// ── The prompt gate ─────────────────────────────────────────────────────────

/**
 * The one fact the ledger is missing before the next ball can be scored.
 *
 * Derived from the fold rather than tracked, which is why it survives undo for free:
 * pop the wicket and `striker` is no longer null, so the "who's in?" prompt simply
 * stops being returned.
 */
export type Prompt =
  | { readonly kind: "batter"; readonly takesStrike: boolean }
  | { readonly kind: "bowler"; readonly overNo: number }
  | { readonly kind: "innings_break" }
  | { readonly kind: "match_over" }
  | { readonly kind: "super_over" }
  | { readonly kind: "super_over_tied" };

export function nextPrompt(draft: MatchDraft, state: MatchState): Prompt | null {
  if (state.result !== null) return { kind: "match_over" };

  const innings = state.innings[state.innings.length - 1];
  const header = state.header;
  if (innings === undefined || header === null) return null;

  // Super over detection: result is null, the last innings ended, and the last two
  // innings form a completed pair with level scores. The engine returns `result === null`
  // when `superOverOnTie` is true and both sides tied — that is the signal.
  if (innings.ended !== null && state.innings.length >= 2 && state.innings.length % 2 === 0) {
    const last = state.innings[state.innings.length - 1]!;
    const penultimate = state.innings[state.innings.length - 2]!;
    if (penultimate.ended !== null && last.runs === penultimate.runs) {
      // Both innings of the pair are done and level. If either is already a super over,
      // this is a *repeated* tie — the scorer decides whether to go again or declare tied.
      if (last.isSuperOver || penultimate.isSuperOver) return { kind: "super_over_tied" };
      return { kind: "super_over" };
    }
  }

  if (innings.ended !== null) return { kind: "innings_break" };

  // When lastManStands and all partners are dismissed, the last man bats alone —
  // no batter prompt, the pad shows.
  const { ruleSet } = draft.setup;
  const lastManAlone = ruleSet.lastManStands && ruleSet.maxWickets === null &&
    header.wickets >= ruleSet.playersPerSide - 1 &&
    (header.striker !== null || header.nonStriker !== null);

  // Openers are two batter prompts in a row, from the same rule that fills an empty
  // slot after a wicket. `takesStrike` is not a guess: it names the slot that is empty.
  if (header.striker === null && !lastManAlone) return { kind: "batter", takesStrike: true };
  if (header.nonStriker === null && !lastManAlone) return { kind: "batter", takesStrike: false };

  // Counting live `NewOver` events against completed overs, rather than watching for
  // an over boundary, is what makes this idempotent under undo. `liveEvents` rather
  // than `ledgerEvents`: a voided `NewOver` (from undoing the last ball of an over
  // plus the bowler selection that followed it) must not count, or the bowler prompt
  // never reappears after the undo.
  const oversStarted = liveEvents(draft).filter(
    (event) => event.inningsNo === innings.inningsNo && event.type === "NewOver",
  ).length;
  if (oversStarted <= header.overs.completed) return { kind: "bowler", overNo: oversStarted };

  return null;
}

/**
 * The crease, or null while the prompt gate still has something to ask.
 *
 * Returning null rather than a partly-filled object is what lets the pad be rendered
 * only when a ball can actually be described — so there is no code path where a
 * delivery is recorded against a missing striker.
 */
export function crease(draft: MatchDraft, state: MatchState): Crease | null {
  const header = state.header;
  const innings = state.innings[state.innings.length - 1];
  if (header === null || innings === undefined || innings.ended !== null) return null;
  if (header.bowler === null) return null;

  const { ruleSet } = draft.setup;
  const lastManAlone = ruleSet.lastManStands && ruleSet.maxWickets === null &&
    header.wickets >= ruleSet.playersPerSide - 1 &&
    (header.striker !== null || header.nonStriker !== null);

  if (lastManAlone) {
    // The survivor, whichever end he is at, faces. His id serves as the non-striker
    // placeholder — the engine fold accepts it and no phantom batting line is created.
    const survivorId = (header.striker ?? header.nonStriker)!.playerId;
    return {
      strikerId: survivorId,
      nonStrikerId: survivorId,
      bowlerId: header.bowler.playerId,
      keeperId: teamById(draft.setup, innings.bowlingTeamId).keeperId,
      freeHit: header.freeHit,
    };
  }

  if (header.striker === null || header.nonStriker === null) return null;

  return {
    strikerId: header.striker.playerId,
    nonStrikerId: header.nonStriker.playerId,
    bowlerId: header.bowler.playerId,
    // The keeper is the fielding side's, and the fielding side changes at the innings
    // break — so this is read off the bowling team every ball rather than captured once.
    keeperId: teamById(draft.setup, innings.bowlingTeamId).keeperId,
    freeHit: header.freeHit,
  };
}

// ── Recording ───────────────────────────────────────────────────────────────

export function buildDelivery(
  draft: MatchDraft,
  state: MatchState,
  crease_: Crease,
  latch: Latch,
  runs: number,
  wicket: Wicket | null,
  /** Where the ball went, when the scorer was asked and answered (ADR-0022). Null otherwise. */
  shot: Shot | null = null,
): Delivery | null {
  const innings = state.innings[state.innings.length - 1];
  if (innings === undefined) return null;

  return build<Delivery>(draft, innings.inningsNo, {
    type: "Delivery",
    // Advisory. `replay()` derives all four itself and ignores what is recorded here,
    // so they are filled in truthfully only to keep a stored payload legible.
    legal: latch.extra === null,
    over: state.header?.overs.completed ?? 0,
    ballInOver: (state.header?.overs.balls ?? 0) + 1,
    strikerId: crease_.strikerId,
    nonStrikerId: crease_.nonStrikerId,
    bowlerId: crease_.bowlerId,
    keeperId: crease_.keeperId,
    runs: buildRuns(latch, runs),
    wicket,
    freeHit: crease_.freeHit,
    // A direction is only ever what the scorer chose on the wheel sheet. Nothing here asks for
    // one, so a ball that skipped it, or a match with the wheel off, is exactly as it was.
    shot,
    // The pitch map is still not captured: it must never slow the fast path.
    pitch: null,
    commentary: null,
  });
}

export const buildBatterIn = (
  draft: MatchDraft,
  inningsNo: InningsNo,
  playerId: PlayerId,
  takesStrike: boolean,
  /** Which way he stands, asked once and then remembered (ADR-0023). Absent when not known. */
  battingHand?: BatterHand,
): Event =>
  build<Event>(draft, inningsNo, {
    type: "BatterIn",
    playerId,
    takesStrike,
    ...(battingHand === undefined ? {} : { battingHand }),
  });

/** How a bowler bowls this over (ADR-0023). Either half may be left out when not known. */
export interface BowlingStyleChoice {
  readonly arm?: BowlingArm;
  readonly type?: BowlingType;
}

const styleFields = (style: BowlingStyleChoice | undefined) => ({
  ...(style?.arm === undefined ? {} : { bowlingArm: style.arm }),
  ...(style?.type === undefined ? {} : { bowlingType: style.type }),
});

export const buildNewOver = (
  draft: MatchDraft,
  inningsNo: InningsNo,
  overNo: number,
  bowlerId: PlayerId,
  /** Why the scorer took a bowler the RuleSet warned against. Null when it warned about
   *  nothing — PRD §6.4 allows the override, but only with a reason attached. */
  overrideReason: string | null = null,
  style?: BowlingStyleChoice,
): NewOver =>
  build<NewOver>(draft, inningsNo, { type: "NewOver", overNo, bowlerId, overrideReason, ...styleFields(style) });

export const buildBowlerChange = (
  draft: MatchDraft,
  inningsNo: InningsNo,
  bowlerId: PlayerId,
  overrideReason: string | null,
  style?: BowlingStyleChoice,
): BowlerChange =>
  build<BowlerChange>(draft, inningsNo, { type: "BowlerChange", bowlerId, overrideReason, ...styleFields(style) });

export const buildRetired = (
  draft: MatchDraft,
  inningsNo: InningsNo,
  playerId: PlayerId,
  reason: BatterRetired["reason"],
): BatterRetired =>
  build<BatterRetired>(draft, inningsNo, { type: "BatterRetired", playerId, reason });

/**
 * Start the second innings, with the target the first innings set.
 *
 * The target is `runs + 1` because a chase is won by *passing* the score — levelling it
 * is a tie. The fold ends the innings the moment `runs >= target`, so an off-by-one
 * here would declare a win on the tying run.
 */
export function buildSecondInnings(draft: MatchDraft, state: MatchState): InningsStart | null {
  const first = state.innings[0];
  if (first === undefined) return null;
  return build<InningsStart>(draft, 2, {
    type: "InningsStart",
    battingTeamId: first.bowlingTeamId,
    bowlingTeamId: first.battingTeamId,
    oversAvailable: draft.setup.ruleSet.overs,
    target: first.runs + 1,
    isSuperOver: false,
    followOn: false,
  });
}

// ── Super over ────────────────────────────────────────────────────────────────

/**
 * Start a super over pair. ICC convention: the team that batted *second* in the
 * preceding pair bats first in the super over.
 *
 * Returns two events — the first and second super over innings starts — but only the
 * first is appended now. The second arrives via `buildSecondSuperOverInnings` after the
 * first super over innings ends, exactly like a normal innings break.
 *
 * Always 1 over. The engine's wicket/all-out rules apply as normal: 3 batters means
 * all-out at 2 wickets — `lastManStands` is a RuleSet flag and does not need special
 * handling here.
 */
export function buildSuperOverInnings(draft: MatchDraft, state: MatchState): InningsStart | null {
  if (state.innings.length < 2) return null;

  // The team that batted second in the last completed pair bats first in the super over.
  const lastInnings = state.innings[state.innings.length - 1]!;
  const battingTeamId = lastInnings.battingTeamId;
  const bowlingTeamId = lastInnings.bowlingTeamId;

  const inningsNo = (state.innings.length + 1) as InningsNo;
  return build<InningsStart>(draft, inningsNo, {
    type: "InningsStart",
    battingTeamId,
    bowlingTeamId,
    oversAvailable: 1,
    target: null,
    isSuperOver: true,
    followOn: false,
  });
}

/**
 * Start the second innings of a super over pair — the chase.
 *
 * Reads the last innings (the first super over innings that just ended) and sets
 * `target = runs + 1`, exactly like a normal second innings.
 */
export function buildSecondSuperOverInnings(
  draft: MatchDraft,
  state: MatchState,
): InningsStart | null {
  const firstSO = state.innings[state.innings.length - 1];
  if (firstSO === undefined || !firstSO.isSuperOver) return null;

  const inningsNo = (state.innings.length + 1) as InningsNo;
  return build<InningsStart>(draft, inningsNo, {
    type: "InningsStart",
    battingTeamId: firstSO.bowlingTeamId,
    bowlingTeamId: firstSO.battingTeamId,
    oversAvailable: 1,
    target: firstSO.runs + 1,
    isSuperOver: true,
    followOn: false,
  });
}

// ── Prospective rule warnings ───────────────────────────────────────────────

/** Discarded immediately, so it never needs a real time-ordered id. */
const PROBE_ID = asId<EventId>("ffffffff-ffff-7fff-8fff-ffffffffffff");

/**
 * Taking the ball at the start of an over, or part-way through one.
 *
 * The distinction matters to the rules: the consecutive-overs rule only applies to a new
 * over, while the per-bowler cap applies however he got there. Probing a mid-over change
 * with a `NewOver` would invent a consecutive-overs warning that is not real.
 */
export type BowlerProbe =
  | { readonly kind: "new_over"; readonly overNo: number }
  | { readonly kind: "mid_over" };

/**
 * What the engine would warn about if this bowler took the ball now.
 *
 * PRD §6.4 requires bowler restrictions to be *warnings the scorer may override*, and a
 * warning is only useful before the tap. Rather than re-deriving "has he bowled his
 * four?" here — a second implementation of a rule the fold already owns (ADR-0001) —
 * this folds a throwaway candidate event and reads back what the engine said.
 *
 * Slicing by the current warning count is sound because the fold only ever pushes:
 * appending an event cannot change or reorder what came before it, so the existing
 * warnings are always a prefix of the new list.
 */
export function bowlerWarnings(
  draft: MatchDraft,
  state: MatchState,
  inningsNo: InningsNo,
  bowlerId: PlayerId,
  probeKind: BowlerProbe,
): readonly RuleWarning[] {
  const ledgerFields = {
    eventId: PROBE_ID,
    matchId: draft.setup.matchId,
    inningsNo,
    seq: Number.MAX_SAFE_INTEGER,
    supersedes: null,
    voided: false,
    provenance: human(draft.setup.scorerId),
    recordedAt: "",
  } as const;

  const probe: NewOver | BowlerChange =
    probeKind.kind === "new_over"
      ? { type: "NewOver", overNo: probeKind.overNo, bowlerId, overrideReason: null, ...ledgerFields }
      : { type: "BowlerChange", bowlerId, overrideReason: null, ...ledgerFields };

  const probed = replay(
    [...ledgerEvents(draft), probe],
    draft.setup.ruleSet,
    draft.setup.matchId,
  );
  return probed.warnings.slice(state.warnings.length);
}
